import {
  DIRECTOR_CONSOLE_DEFAULT_PANORAMA_RESOLUTION,
  DIRECTOR_CONSOLE_PANORAMA_API_NAME,
  DIRECTOR_CONSOLE_PANORAMA_EFFECT_IDS,
  DIRECTOR_CONSOLE_PANORAMA_RATIO,
  DIRECTOR_CONSOLE_STATE_VERSION,
  type DirectorConsoleCameraShot,
  type DirectorConsoleGeometryKind,
  type DirectorConsoleModelLoadStatus,
  type DirectorConsoleNodeData,
  type DirectorConsoleObjectKind,
  type DirectorConsoleObjectState,
  type DirectorConsolePanoramaMeta,
  type DirectorConsolePanoramaResolution,
  type DirectorConsolePanelState,
  type DirectorConsolePanelRight,
  type DirectorConsolePersistedState,
  type DirectorConsoleScenePanorama,
  type DirectorConsoleSceneState,
  type DirectorConsoleVector3,
} from './types';

const DEFAULT_SKY_COLOR = '#08090c';
const DEFAULT_OBJECT_COLOR = '#8fb8ff';

const DEFAULT_PANEL_STATE: DirectorConsolePanelState = {
  leftTab: 'scene',
  rightPanel: 'scene',
  cameraViewMode: 'compact',
};

const DEFAULT_SCENE_STATE: DirectorConsoleSceneState = {
  zoom: 1,
  pan: [0, 0, 0],
  rotation: [24, 28, 0],
  skyColor: DEFAULT_SKY_COLOR,
  frameAspectRatio: '16:9',
  labelsVisible: true,
  gridSnapping: false,
  ground: {
    visible: true,
    opacity: 0.4,
    height: 0,
  },
};

/** 创建轻量 ID，供未提交后端的本地导演台对象和截图记录使用。 */
export function createDirectorConsoleId(prefix: string): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** 判断未知值是否为普通对象，用于恢复旧 JSON 时做字段保护。 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** 将未知值收敛为有限数字，避免坏 JSON 破坏 three.js 坐标计算。 */
function toFiniteNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/** 将未知数组恢复成 3D 向量。 */
function toVector3(value: unknown, fallback: DirectorConsoleVector3): DirectorConsoleVector3 {
  if (!Array.isArray(value)) return fallback;
  return [
    toFiniteNumber(value[0], fallback[0]),
    toFiniteNumber(value[1], fallback[1]),
    toFiniteNumber(value[2], fallback[2]),
  ];
}

/** 将未知字符串恢复成合法的导演台对象类型。 */
function normalizeObjectKind(value: unknown): DirectorConsoleObjectKind {
  if (value === 'geometry' || value === 'model' || value === 'role' || value === 'prop' || value === 'camera') {
    return value;
  }
  return 'geometry';
}

/** 将未知字符串恢复成合法的几何类型。 */
function normalizeGeometryKind(value: unknown): DirectorConsoleGeometryKind {
  if (value === 'sphere' || value === 'cylinder' || value === 'plane') {
    return value;
  }
  return 'box';
}

/** 将未知字符串恢复成合法的模型加载状态。 */
function normalizeModelLoadStatus(value: unknown): DirectorConsoleModelLoadStatus | undefined {
  if (value === 'success' || value === 'failed' || value === 'uploading' || value === 'parsing' || value === 'loading') {
    return value;
  }
  return undefined;
}

/** 将未知对象恢复为姿势滑杆值表，丢弃不可序列化或非法数值。 */
function normalizeRolePoseControls(value: unknown): Record<string, number> | undefined {
  if (!isRecord(value)) return undefined;
  const controls: Record<string, number> = {};
  Object.entries(value).forEach(([key, item]) => {
    if (typeof key === 'string' && typeof item === 'number' && Number.isFinite(item)) {
      controls[key] = item;
    }
  });
  return Object.keys(controls).length > 0 ? controls : undefined;
}

/** 根据对象类型决定新增和选中时默认打开的右侧面板。 */
function getRightPanelForObjectKind(kind: DirectorConsoleObjectKind | undefined): DirectorConsolePanelRight {
  if (kind === 'camera') return 'camera';
  if (kind === 'role') return 'role';
  if (kind) return 'object';
  return 'scene';
}

/** 创建全景生成节点的持久化标记。 */
export function createDirectorPanoramaMeta(
  resolution = DIRECTOR_CONSOLE_DEFAULT_PANORAMA_RESOLUTION,
): DirectorConsolePanoramaMeta {
  const normalizedResolution: DirectorConsolePanoramaResolution = resolution === '1K' || resolution === '4K'
    ? resolution
    : '2K';
  return {
    isPanorama720: true,
    apiName: DIRECTOR_CONSOLE_PANORAMA_API_NAME,
    effectId: DIRECTOR_CONSOLE_PANORAMA_EFFECT_IDS[normalizedResolution],
    ratio: DIRECTOR_CONSOLE_PANORAMA_RATIO,
    resolution: normalizedResolution,
  };
}

/** 创建 720 全景结果节点所需的初始数据，确保所有生成入口使用同一份预览标记。 */
export function createDirectorPanoramaResultNodeData(
  resolution = DIRECTOR_CONSOLE_DEFAULT_PANORAMA_RESOLUTION,
) {
  const panoramaMeta = createDirectorPanoramaMeta(resolution);
  return {
    panoramaMeta,
    panoramaResolution: panoramaMeta.resolution,
    panoramaPreviewMode: true,
  };
}

/** 创建导演台默认 JSON 状态。 */
export function createDefaultDirectorConsoleState(): DirectorConsolePersistedState {
  return {
    scene: {
      ...DEFAULT_SCENE_STATE,
      pan: [...DEFAULT_SCENE_STATE.pan],
      rotation: [...DEFAULT_SCENE_STATE.rotation],
      ground: { ...DEFAULT_SCENE_STATE.ground },
    },
    objects: [],
    selectedIds: [],
    activeTool: 'move',
    activeCameraId: '',
    cameraShots: [],
    panel: { ...DEFAULT_PANEL_STATE },
  };
}

/** 从未知 JSON 恢复全景背景字段。 */
function normalizeScenePanorama(value: unknown): DirectorConsoleScenePanorama | undefined {
  if (!isRecord(value)) return undefined;
  const url = typeof value.url === 'string' ? value.url : '';
  if (!url) return undefined;
  return {
    assetId: typeof value.assetId === 'string' || typeof value.assetId === 'number' ? value.assetId : undefined,
    url,
    width: toFiniteNumber(value.width, 0),
    height: toFiniteNumber(value.height, 0),
    source: value.source === 'upload' || value.source === 'manual' ? value.source : 'canvas_connection',
    sphereRotation: toFiniteNumber(value.sphereRotation, 0),
    sphereRadius: toFiniteNumber(value.sphereRadius, 3),
  };
}

/** 从图片节点 data 中解析导演台背景资源；连入导演台的图片统一按全景图处理。 */
export function resolveDirectorPanoramaFromImageNodeData(value: unknown): DirectorConsoleScenePanorama | undefined {
  if (!isRecord(value)) return undefined;
  const mediaList = Array.isArray(value.media_list) ? value.media_list : [];
  const image = mediaList.find(isRecord);
  if (!image) return undefined;
  let url = '';
  if (typeof image?.originUrl === 'string' && image.originUrl) {
    url = image.originUrl;
  } else if (typeof image?.url === 'string') {
    url = image.url;
  }
  if (!url) return undefined;
  return {
    assetId: typeof image.assetId === 'string' || typeof image.assetId === 'number' ? image.assetId : undefined,
    url,
    width: toFiniteNumber(image.width, 0),
    height: toFiniteNumber(image.height, 0),
    source: 'canvas_connection',
    sphereRotation: 0,
    sphereRadius: 8,
  };
}

/** 从未知 JSON 恢复场景字段，缺失字段按默认导演台状态补齐。 */
function normalizeSceneState(value: unknown): DirectorConsoleSceneState {
  if (!isRecord(value)) {
    return createDefaultDirectorConsoleState().scene;
  }
  const ground = isRecord(value.ground) ? value.ground : {};
  return {
    zoom: toFiniteNumber(value.zoom, DEFAULT_SCENE_STATE.zoom),
    pan: toVector3(value.pan, DEFAULT_SCENE_STATE.pan),
    rotation: toVector3(value.rotation, DEFAULT_SCENE_STATE.rotation),
    skyColor: typeof value.skyColor === 'string' ? value.skyColor : DEFAULT_SCENE_STATE.skyColor,
    frameAspectRatio: typeof value.frameAspectRatio === 'string' ? value.frameAspectRatio : DEFAULT_SCENE_STATE.frameAspectRatio,
    panorama: normalizeScenePanorama(value.panorama),
    labelsVisible: typeof value.labelsVisible === 'boolean' ? value.labelsVisible : DEFAULT_SCENE_STATE.labelsVisible,
    gridSnapping: typeof value.gridSnapping === 'boolean' ? value.gridSnapping : DEFAULT_SCENE_STATE.gridSnapping,
    ground: {
      visible: typeof ground.visible === 'boolean' ? ground.visible : DEFAULT_SCENE_STATE.ground.visible,
      opacity: toFiniteNumber(ground.opacity, DEFAULT_SCENE_STATE.ground.opacity),
      height: toFiniteNumber(ground.height, DEFAULT_SCENE_STATE.ground.height),
    },
  };
}

/** 从未知 JSON 恢复单个导演台对象。 */
function normalizeObjectState(value: unknown): DirectorConsoleObjectState | undefined {
  if (!isRecord(value)) return undefined;
  const id = typeof value.id === 'string' && value.id ? value.id : createDirectorConsoleId('object');
  const kind = normalizeObjectKind(value.kind);
  return {
    id,
    name: typeof value.name === 'string' && value.name ? value.name : '未命名对象',
    kind,
    geometryKind: normalizeGeometryKind(value.geometryKind),
    fileName: typeof value.fileName === 'string' ? value.fileName : undefined,
    format: typeof value.format === 'string' ? value.format : undefined,
    size: typeof value.size === 'number' ? value.size : undefined,
    url: typeof value.url === 'string' ? value.url : undefined,
    assetId: typeof value.assetId === 'string' || typeof value.assetId === 'number' ? value.assetId : undefined,
    loadStatus: normalizeModelLoadStatus(value.loadStatus),
    errorMessage: typeof value.errorMessage === 'string' ? value.errorMessage : undefined,
    visible: typeof value.visible === 'boolean' ? value.visible : true,
    locked: typeof value.locked === 'boolean' ? value.locked : false,
    cameraFocalLength: typeof value.cameraFocalLength === 'string' ? value.cameraFocalLength : undefined,
    cameraTarget: typeof value.cameraTarget === 'string' ? value.cameraTarget : undefined,
    cameraTargetPosition: Array.isArray(value.cameraTargetPosition) ? toVector3(value.cameraTargetPosition, [0, 0, 0]) : undefined,
    rolePosePreset: typeof value.rolePosePreset === 'string' ? value.rolePosePreset : undefined,
    rolePoseControls: normalizeRolePoseControls(value.rolePoseControls),
    position: toVector3(value.position, [0, 0, 0]),
    rotation: toVector3(value.rotation, [0, 0, 0]),
    scale: toVector3(value.scale, [1, 1, 1]),
    uniformScale: toFiniteNumber(value.uniformScale, 1),
    color: typeof value.color === 'string' ? value.color : DEFAULT_OBJECT_COLOR,
  };
}

/** 从未知 JSON 恢复单条截图记录。 */
function normalizeCameraShot(value: unknown): DirectorConsoleCameraShot | undefined {
  if (!isRecord(value)) return undefined;
  const url = typeof value.url === 'string' ? value.url : '';
  if (!url) return undefined;
  return {
    id: typeof value.id === 'string' && value.id ? value.id : createDirectorConsoleId('shot'),
    cameraId: typeof value.cameraId === 'string' ? value.cameraId : '',
    title: typeof value.title === 'string' && value.title ? value.title : '截图',
    imageNodeId: typeof value.imageNodeId === 'string' ? value.imageNodeId : undefined,
    assetId: typeof value.assetId === 'string' || typeof value.assetId === 'number' ? value.assetId : undefined,
    url,
    width: toFiniteNumber(value.width, 0),
    height: toFiniteNumber(value.height, 0),
    createdAt: toFiniteNumber(value.createdAt, Date.now()),
  };
}

/** 从未知 JSON 恢复右侧/左侧面板状态。 */
function normalizePanelState(value: unknown): DirectorConsolePanelState {
  if (!isRecord(value)) return { ...DEFAULT_PANEL_STATE };
  const panelSet = new Set<DirectorConsolePanelRight>(['scene', 'object', 'camera', 'cameraShots', 'role', 'rolePose']);
  let cameraViewMode: DirectorConsolePanelState['cameraViewMode'] = 'compact';
  if (
    value.cameraViewMode === 'expanded'
    || value.cameraViewMode === 'fullA'
    || value.cameraViewMode === 'fullB'
    || value.cameraViewMode === 'split'
  ) {
    cameraViewMode = value.cameraViewMode;
  }
  return {
    leftTab: value.leftTab === 'assets' ? 'assets' : 'scene',
    rightPanel: typeof value.rightPanel === 'string' && panelSet.has(value.rightPanel as DirectorConsolePanelRight)
      ? value.rightPanel as DirectorConsolePanelRight
      : 'scene',
    cameraViewMode,
  };
}

/** 从节点 data 解析可用于渲染的导演台状态，并对旧/坏 JSON 做兜底。 */
export function normalizeDirectorConsoleState(data?: Partial<DirectorConsoleNodeData> | null): DirectorConsolePersistedState {
  const defaultState = createDefaultDirectorConsoleState();
  const raw = data?.directorConsoleState;
  if (!isRecord(raw)) return defaultState;

  const objects = Array.isArray(raw.objects)
    ? raw.objects.map(normalizeObjectState).filter((item): item is DirectorConsoleObjectState => Boolean(item))
    : [];
  const objectIdSet = new Set(objects.map((item) => item.id));
  const selectedIds = Array.isArray(raw.selectedIds)
    ? raw.selectedIds.filter((id): id is string => typeof id === 'string' && objectIdSet.has(id))
    : [];
  const cameraShots = Array.isArray(raw.cameraShots)
    ? raw.cameraShots.map(normalizeCameraShot).filter((item): item is DirectorConsoleCameraShot => Boolean(item))
    : [];
  const activeCameraId = typeof raw.activeCameraId === 'string' && objectIdSet.has(raw.activeCameraId)
    ? raw.activeCameraId
    : '';
  return {
    scene: normalizeSceneState(raw.scene),
    objects,
    selectedIds,
    activeTool: raw.activeTool === 'rotate' || raw.activeTool === 'scale' ? raw.activeTool : 'move',
    activeCameraId,
    cameraShots,
    panel: normalizePanelState(raw.panel),
  };
}

/** 将导演台状态包装成节点 data 可持久化字段。 */
export function createDirectorConsoleDataPatch(state: DirectorConsolePersistedState): Partial<DirectorConsoleNodeData> {
  return {
    directorConsoleStateVersion: DIRECTOR_CONSOLE_STATE_VERSION,
    directorConsoleState: state,
  };
}

/** 新建几何对象并落在世界坐标原点附近。 */
export function createDirectorGeometry(kind: DirectorConsoleGeometryKind): DirectorConsoleObjectState {
  const labelMap: Record<DirectorConsoleGeometryKind, string> = {
    box: '立方体',
    sphere: '球体',
    cylinder: '圆柱体',
    plane: '平面',
    torus: '环状体',
    cone: '圆锥',
    pyramid: '棱锥',
  };
  return {
    id: createDirectorConsoleId(kind),
    name: labelMap[kind],
    kind: 'geometry',
    geometryKind: kind,
    position: [0, 0, 0],
    rotation: [0, 0, 0],
    scale: [1, 1, 1],
    uniformScale: 1,
    color: DEFAULT_OBJECT_COLOR,
    loadStatus: 'success',
    visible: true,
    locked: false,
  };
}

/** 新建模型占位对象，真实资源接口未落地前先保存文件元信息并等待本地 loader 回写状态。 */
export function createDirectorModelObject(file: File): DirectorConsoleObjectState {
  const suffix = file.name.split('.').pop()?.toLowerCase() ?? '';
  return {
    id: createDirectorConsoleId('model'),
    name: file.name.replace(/\.[^.]+$/, '') || '上传模型',
    kind: 'model',
    fileName: file.name,
    format: suffix,
    size: file.size,
    position: [0, 0, 0],
    rotation: [0, 0, 0],
    scale: [1, 1, 1],
    uniformScale: 1,
    color: '#b7d1ff',
    loadStatus: 'loading',
    visible: true,
    locked: false,
  };
}

/** 新建机位对象，保存为可调参数并供截图归属使用。 */
export function createDirectorCamera(): DirectorConsoleObjectState {
  return {
    id: createDirectorConsoleId('camera'),
    name: '机位',
    kind: 'camera',
    position: [0, 2.2, 4.5],
    rotation: [-12, 0, 0],
    scale: [1, 1, 1],
    uniformScale: 1,
    color: '#ffcf70',
    cameraFocalLength: '14mm',
    cameraTarget: '手动坐标',
    cameraTargetPosition: [0, 0, 0],
    loadStatus: 'success',
    visible: true,
    locked: false,
  };
}

/** 新建角色素体对象，素体资源接口未接入前先生成可编辑占位角色。 */
export function createDirectorRole(name: string): DirectorConsoleObjectState {
  return {
    id: createDirectorConsoleId('role'),
    name,
    kind: 'role',
    geometryKind: 'cylinder',
    position: [0, 0, 0],
    rotation: [0, 0, 0],
    scale: [1, 1, 1],
    uniformScale: 1,
    color: '#4d7dff',
    rolePosePreset: '站立',
    loadStatus: 'success',
    visible: true,
    locked: false,
  };
}

/** 新建道具对象，道具 fbx 资源接入前先生成可编辑占位体。 */
export function createDirectorProp(name: string): DirectorConsoleObjectState {
  return {
    id: createDirectorConsoleId('prop'),
    name,
    kind: 'prop',
    geometryKind: 'box',
    position: [0, 0.5, 0],
    rotation: [0, 0, 0],
    scale: [1, 1, 1],
    uniformScale: 1,
    color: '#d8c1a9',
    loadStatus: 'success',
    visible: true,
    locked: false,
  };
}

/** 向状态追加对象，并选中新对象。 */
export function addDirectorObject(
  state: DirectorConsolePersistedState,
  object: DirectorConsoleObjectState,
): DirectorConsolePersistedState {
  return {
    ...state,
    objects: [...state.objects, object],
    selectedIds: [object.id],
    activeCameraId: object.kind === 'camera' ? object.id : state.activeCameraId,
    panel: {
      ...state.panel,
      rightPanel: getRightPanelForObjectKind(object.kind),
    },
  };
}

/** 按对象 ID 更新导演台对象参数。 */
export function updateDirectorObject(
  state: DirectorConsolePersistedState,
  objectId: string,
  patch: Partial<DirectorConsoleObjectState>,
): DirectorConsolePersistedState {
  return {
    ...state,
    objects: state.objects.map((object) => (object.id === objectId ? { ...object, ...patch, id: object.id } : object)),
  };
}

/** 按对象 ID 删除导演台对象，并同步清理选中和活跃机位。 */
export function removeDirectorObject(
  state: DirectorConsolePersistedState,
  objectId: string,
): DirectorConsolePersistedState {
  return {
    ...state,
    objects: state.objects.filter((object) => object.id !== objectId),
    selectedIds: state.selectedIds.filter((id) => id !== objectId),
    activeCameraId: state.activeCameraId === objectId ? '' : state.activeCameraId,
    cameraShots: state.cameraShots.filter((shot) => shot.cameraId !== objectId),
  };
}

/** 更新导演台选中对象，并根据对象类型切换右侧参数面板。 */
export function selectDirectorObject(
  state: DirectorConsolePersistedState,
  objectId: string,
): DirectorConsolePersistedState {
  const selectedObject = state.objects.find((object) => object.id === objectId);
  const rightPanel = getRightPanelForObjectKind(selectedObject?.kind);
  return {
    ...state,
    selectedIds: selectedObject ? [objectId] : [],
    activeCameraId: selectedObject?.kind === 'camera' ? objectId : state.activeCameraId,
    panel: {
      ...state.panel,
      rightPanel,
    },
  };
}

/** 更新导演台场景参数。 */
export function patchDirectorScene(
  state: DirectorConsolePersistedState,
  patch: Partial<DirectorConsoleSceneState>,
): DirectorConsolePersistedState {
  return {
    ...state,
    scene: {
      ...state.scene,
      ...patch,
      ground: {
        ...state.scene.ground,
        ...patch.ground,
      },
    },
  };
}

/** 将输入图片节点解析出的全景背景写入导演台场景。 */
export function setDirectorPanorama(
  state: DirectorConsolePersistedState,
  panorama: DirectorConsoleScenePanorama | undefined,
): DirectorConsolePersistedState {
  return {
    ...state,
    scene: {
      ...state.scene,
      panorama,
    },
  };
}

/** 修改导演台 UI 面板状态并持久化。 */
export function patchDirectorPanel(
  state: DirectorConsolePersistedState,
  patch: Partial<DirectorConsolePanelState>,
): DirectorConsolePersistedState {
  return {
    ...state,
    panel: {
      ...state.panel,
      ...patch,
    },
  };
}

/** 确保截图有机位归属；无机位时自动创建一个默认机位。 */
export function ensureDirectorCameraForShot(state: DirectorConsolePersistedState): {
  state: DirectorConsolePersistedState;
  cameraId: string;
} {
  const existingCamera = state.activeCameraId
    ? state.objects.find((object) => object.id === state.activeCameraId && object.kind === 'camera')
    : state.objects.find((object) => object.kind === 'camera');
  if (existingCamera) {
    return {
      state: {
        ...state,
        activeCameraId: existingCamera.id,
      },
      cameraId: existingCamera.id,
    };
  }

  const camera = createDirectorCamera();
  return {
    state: addDirectorObject(state, camera),
    cameraId: camera.id,
  };
}

/** 创建截图记录，截图 URL 可能先是本地 blob，上传成功后再回写资产字段。 */
export function createDirectorCameraShot(params: {
  id?: string;
  cameraId: string;
  imageNodeId?: string;
  url: string;
  width: number;
  height: number;
}): DirectorConsoleCameraShot {
  return {
    id: params.id ?? createDirectorConsoleId('shot'),
    cameraId: params.cameraId,
    title: `截图 ${new Date().toLocaleTimeString('zh-CN', { hour12: false })}`,
    imageNodeId: params.imageNodeId,
    url: params.url,
    width: params.width,
    height: params.height,
    createdAt: Date.now(),
  };
}

/** 上传完成后回写截图资源身份，确保刷新后使用最终资产 URL 和 assetId。 */
export function updateDirectorCameraShotResource(
  state: DirectorConsolePersistedState,
  shotId: string,
  patch: Partial<Pick<DirectorConsoleCameraShot, 'imageNodeId' | 'assetId' | 'url' | 'width' | 'height'>>,
): DirectorConsolePersistedState {
  return {
    ...state,
    cameraShots: state.cameraShots.map((shot) => (shot.id === shotId ? { ...shot, ...patch } : shot)),
  };
}

/** 追加截图记录，并切到截图所属机位。 */
export function appendDirectorCameraShot(
  state: DirectorConsolePersistedState,
  shot: DirectorConsoleCameraShot,
): DirectorConsolePersistedState {
  return {
    ...state,
    activeCameraId: shot.cameraId,
    selectedIds: [shot.cameraId],
    cameraShots: [...state.cameraShots, shot],
    panel: {
      ...state.panel,
      rightPanel: 'cameraShots',
    },
  };
}
